import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { INK, LINE, MUTED, PAPER, SHADOW_FLOAT } from './theme.ts';
import { Icon } from './Icon.tsx';

/** the class Paddle renders its inline checkout into */
export const CHECKOUT_FRAME = 'fk-paddle-frame';

/**
 * Paddle's own "overlay" mode replaces the whole screen, which loses the plan
 * page — and with it the sense that the offer is still there to come back to.
 * This is the checkout in Paddle's `inline` mode, inside a dialog that dims the
 * plan page rather than hiding it.
 */
/** scroll the open checkout back to its top — where Paddle shows a declined-payment message */
export function scrollCheckoutToTop(): void {
  const sheet = document.querySelector<HTMLElement>('[role="dialog"][aria-modal="true"]');
  sheet?.scrollTo({ top: 0, behavior: 'smooth' });
}

export function CheckoutDialog({ title, onClose }: { title: string; onClose: () => void }) {
  const card = useRef<HTMLDivElement | null>(null);
  // the plan page behind re-renders every second (the reservation timer), which
  // hands this a fresh onClose each time. Keeping it in a ref is what stops the
  // effect re-running and stealing focus back from Paddle's card fields.
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    card.current?.focus();

    // The sheet is a page of its own to the browser's back button: opening it
    // pushes a history entry, back pops it and closes the sheet — the plan page
    // stays. Closing it any other way pops that entry so back still works.
    let popped = false;
    history.pushState({ fkCheckout: true }, '');
    const onPop = () => {
      popped = true;
      close.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      window.removeEventListener('popstate', onPop);
      if (!popped && (history.state as { fkCheckout?: boolean } | null)?.fkCheckout) history.back();
    };
  }, []);

  // Rendered on <body>, outside the fixed shell and its scrolling step area:
  // inside them a fixed sheet is measured against the step area on iOS and its
  // top ends up under the funnel header.
  return createPortal(
    <div
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) close.current();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        top: 'var(--fk-vv-top, 0px)',
        height: 'var(--fk-vv-height, auto)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(31,27,45,.55)',
        backdropFilter: 'blur(3px)',
        animation: 'fk-fill .2s ease-out',
      }}
    >
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '92%',
          overflow: 'auto',
          overscrollBehavior: 'contain',
          background: PAPER,
          borderRadius: '28px 28px 0 0',
          boxShadow: SHADOW_FLOAT,
          padding: '0 16px calc(16px + env(safe-area-inset-bottom))',
          animation: 'fk-in .3s ease-out',
          outline: 'none',
        }}
      >
        {/* pinned: the close button must stay reachable however far the form scrolls */}
        <div style={{ position: 'sticky', top: 0, zIndex: 1, background: PAPER, display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0 10px', borderBottom: `2px solid ${LINE}` }}>
          <span style={{ flex: 1, fontWeight: 800, fontSize: 17, color: INK }}>{title}</span>
          <button
            type="button"
            onClick={() => close.current()}
            aria-label="Close checkout"
            style={{ width: 36, height: 36, borderRadius: '50%', border: `2px solid ${LINE}`, background: PAPER, cursor: 'pointer', color: MUTED }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        {/* Paddle mounts its iframe here */}
        <div className={CHECKOUT_FRAME} style={{ minHeight: 420, marginTop: 8 }} />
      </div>
    </div>,
    document.body,
  );
}
